import { BREAKPOINTS, COLORS, ComponentL, IconButton, SystemIcons } from '@laerdal/life-react-components';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectLocale, selectMarket, setMarket } from '../store/slices/globalConfigSlice';
import { marketList } from '../model/constants/marketContants';
import { getPathWithoutMarket } from '../model/constants/marketUrl';
import { useNavigate } from 'react-router-dom';
import { useMediaMatch } from "rooks";


const Container = styled.div`
  background: ${COLORS.neutral_700};;
  display: flex;
  
  height: 55px;
  
  align-items: center;
`;

const MainButton = styled.div`
  ${BREAKPOINTS.MEDIUM}{
    margin: 8px  50px;
    padding: 0px 16px;
  }
  margin: 8px 0px;
  padding: 0px 16px;

  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  cursor: pointer;
  color: ${COLORS.primary_20};

  &:hover {
    border-radius: 4px;
    background: ${COLORS.primary_50};
    color: ${COLORS.primary_700};
  }
`;

const DropdownContainer = styled.div<{ $isOpen: boolean }>`
  ${BREAKPOINTS.MEDIUM}{
    flex-direction: row;
    padding: 64px;
    width: calc(100% - 124px);
  }
  padding: 16px;
  width: calc(100% - 32px);
  flex-direction: column;

  border-top: 1px solid ${COLORS.primary_200};
  position: absolute;
  top: 55px;
  background: ${COLORS.black};
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  z-index: 100000;
  
  

  color: ${COLORS.white};
`;

const H3 = styled.div`
  flex-direction: column;
  display: flex;
  max-width: 275px;
  flex-grow: 1;
  gap: 16px;
  font-size: 40px;
`;

const Line = styled.div`
  width: 79px;
  height: 1px;
  background: ${COLORS.white};
`;

const Column = styled.div`
  flex-direction: column;
  display: flex;
  max-width: 300px;
  flex-grow: 1;
  gap: 16px;
`;

const Country = styled.div`
  
  padding: 4px 8px;
  justify-content: space-between;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  cursor: pointer;
  color: ${COLORS.primary_20};

  &:hover {
    border-radius: 4px;
    background: ${COLORS.primary_50};
    color: ${COLORS.primary_700};

    div {
      color: ${COLORS.primary_700};
    }
  }
`;
const CountryLabel = styled.div`
  justify-content: start;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  align-self: stretch;
  cursor: pointer;
  color: ${COLORS.primary_20};
`;



const LanguageSelector = () => {
  const { t } = useTranslation('translation');
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate()
  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));

  const currentMartket = useSelector(selectMarket);
  const currentLocale = useSelector(selectLocale);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountryClick = (marketCode: string) => {
    const pathWithoutMarket = getPathWithoutMarket(location.pathname);

    dispatch(setMarket(marketCode));
    setIsOpen(false);

    navigate(`/${marketCode}${pathWithoutMarket}${location.search}${location.hash}`)
  };

  return (
    <Container ref={containerRef}>
      <MainButton onClick={() => setIsOpen(!isOpen)}>
        {marketList[currentMartket]?.icon}
        <div>
          {marketList[currentMartket]?.marketName}
        </div>
        <SystemIcons.ArrowDropDown size='24px' />
      </MainButton>
      <DropdownContainer $isOpen={isOpen}>
        {isMediumScreen && <Column>
          <IconButton
            variant='secondary'
            useTransparentBackground={true}
            shape='circular'
            action={() => setIsOpen(false)}  >
            <SystemIcons.Close />
          </IconButton>
          <Line />
          <H3>
            Country & language selector
          </H3>
        </Column>}

        <Column>
          <ComponentL color={COLORS.white}>Americas</ComponentL>
          <Country onClick={() => handleCountryClick('la')}>
            <CountryLabel>{marketList['la'].icon} {marketList['la'].marketName}</CountryLabel>
            {currentMartket == 'la' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('br')}>
            <CountryLabel>{marketList['br'].icon} {marketList['br'].marketName}</CountryLabel>
            {currentMartket == 'br' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('ca')}>
            <CountryLabel>{marketList['ca'].icon} {marketList['ca'].marketName}</CountryLabel>
            {currentMartket == 'ca' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('mx')}>
            <CountryLabel>{marketList['mx'].icon} {marketList['mx'].marketName}</CountryLabel>
            {currentMartket == 'mx' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('us')}>
            <CountryLabel>{marketList['us'].icon} {marketList['us'].marketName}</CountryLabel>
            {currentMartket == 'us' && <SystemIcons.CheckMark size='24px' />}
          </Country>

          <ComponentL color={COLORS.white}>Asia / Pacific</ComponentL>
          <Country onClick={() => handleCountryClick('au')}>
            <CountryLabel>{marketList['au'].icon} {marketList['au'].marketName}</CountryLabel>
            {currentMartket == 'au' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('cn')}>
            <CountryLabel>{marketList['cn'].icon} {marketList['cn'].marketName}</CountryLabel>
            {currentMartket == 'cn' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('hk')}>
            <CountryLabel>{marketList['hk'].icon} {marketList['hk'].marketName}</CountryLabel>
            {currentMartket == 'hk' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('in')}>
            <CountryLabel>{marketList['in'].icon} {marketList['in'].marketName}</CountryLabel>
            {currentMartket == 'in' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('jp')}>
            <CountryLabel>{marketList['jp'].icon} {marketList['jp'].marketName}</CountryLabel>
            {currentMartket == 'jp' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('kr')}>
            <CountryLabel>{marketList['kr'].icon} {marketList['kr'].marketName}</CountryLabel>
            {currentMartket == 'kr' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('my')}>
            <CountryLabel>{marketList['my'].icon} {marketList['my'].marketName}</CountryLabel>
            {currentMartket == 'my' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('nz')}>
            <CountryLabel>{marketList['nz'].icon} {marketList['nz'].marketName}</CountryLabel>
            {currentMartket == 'nz' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('sg')}>
            <CountryLabel>{marketList['sg'].icon} {marketList['sg'].marketName}</CountryLabel>
            {currentMartket == 'sg' && <SystemIcons.CheckMark size='24px' />}
          </Country>
        </Column>

        <Column>
          <ComponentL color={COLORS.white}>Europe</ComponentL>
          <Country onClick={() => handleCountryClick('be-nl')}>
            <CountryLabel>{marketList['be-nl'].icon} {marketList['be-nl'].marketName}</CountryLabel>
            {currentMartket == 'be-nl' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('be-fr')}>
            <CountryLabel>{marketList['be-fr'].icon} {marketList['be-fr'].marketName}</CountryLabel>
            {currentMartket == 'be-fr' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('dk')}>
            <CountryLabel>{marketList['dk'].icon} {marketList['dk'].marketName}</CountryLabel>
            {currentMartket == 'dk' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('de')}>
            <CountryLabel>{marketList['de'].icon} {marketList['de'].marketName}</CountryLabel>
            {currentMartket == 'de' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('es')}>
            <CountryLabel>{marketList['es'].icon} {marketList['es'].marketName}</CountryLabel>
            {currentMartket == 'es' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('fr')}>
            <CountryLabel>{marketList['fr'].icon} {marketList['fr'].marketName}</CountryLabel>
            {currentMartket == 'fr' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('it')}>
            <CountryLabel>{marketList['it'].icon} {marketList['it'].marketName}</CountryLabel>
            {currentMartket == 'it' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('nl')}>
            <CountryLabel>{marketList['nl'].icon} {marketList['nl'].marketName}</CountryLabel>
            {currentMartket == 'nl' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('no')}>
            <CountryLabel>{marketList['no'].icon} {marketList['no'].marketName}</CountryLabel>
            {currentMartket == 'no' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('at')}>
            <CountryLabel>{marketList['at'].icon} {marketList['at'].marketName}</CountryLabel>
            {currentMartket == 'at' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('pl')}>
            <CountryLabel>{marketList['pl'].icon} {marketList['pl'].marketName}</CountryLabel>
            {currentMartket == 'pl' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('ch')}>
            <CountryLabel>{marketList['ch'].icon} {marketList['ch'].marketName}</CountryLabel>
            {currentMartket == 'ch' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('fi')}>
            <CountryLabel>{marketList['fi'].icon} {marketList['fi'].marketName}</CountryLabel>
            {currentMartket == 'fi' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('se')}>
            <CountryLabel>{marketList['se'].icon} {marketList['se'].marketName}</CountryLabel>
            {currentMartket == 'se' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('gb')}>
            <CountryLabel>{marketList['gb'].icon} {marketList['gb'].marketName}</CountryLabel>
            {currentMartket == 'gb' && <SystemIcons.CheckMark size='24px' />}
          </Country>
        </Column>

        <Column>
          <ComponentL color={COLORS.white}>Location not listed?</ComponentL>
          <Country onClick={() => handleCountryClick('int')}>
            <CountryLabel>{marketList['int'].icon} {marketList['int'].marketName}</CountryLabel>
            {currentMartket == 'int' && <SystemIcons.CheckMark size='24px' />}
          </Country>
          <Country onClick={() => handleCountryClick('distributors')}>
            <CountryLabel>{marketList['distributors'].icon} {marketList['distributors'].marketName}</CountryLabel>
            {currentMartket == 'distributors' && <SystemIcons.CheckMark size='24px' />}
          </Country>
        </Column>

      </DropdownContainer>
    </Container>
  );
}

export default LanguageSelector;